/**
 * Returns an object with the key and name of a component based on a given color type.
 *
 * @param {string} colorType - The color type to get the component info for. Can be 'vent_color', 'door_color', 'windows_color', or 'shutters_color'.
 *
 * @returns {{ key: string, name: string }} An object with the key and name of the component.
 */
export const getComponentInfoByColorType = (colorType) => {
	const componentMap = {
		vent_color: { key: 'vent', name: 'Vents' },
		door_color: { key: 'door', name: 'Doors' },
		windows_color: { key: 'windows', name: 'Windows' },
		shutters_color: { key: 'shutters', name: 'Shutters' },
	};

	return componentMap[colorType];
};

/**
 * Creates a list of condition objects from an array of items.
 * @param {Object[]} items - An array of objects with the following properties:
 * - baseFactor: The key of the factor to compare.
 * - operator: The operator to use for the comparison. Can be 'or', 'and', 'not', 'in', or 'not_in'.
 * - compareFactor: An array of keys to compare the base factor to.
 * @returns {Object[]} A list of objects with the following properties:
 * - baseFactor: The key of the factor to compare.
 * - operator: The operator to use for the comparison. Can be 'or', 'and', 'not', 'in', or 'not_in'.
 * - compareFactor: A single key to compare the base factor to.
 */
export const getConditionList = (items) => {
	const conditionList = [];
	for (let j = 0; j < items.length; j++) {
		const { baseFactor, operator, compareFactor } = items[j];
		if (baseFactor && operator && compareFactor.length) {
			for (let k = 0; k < compareFactor.length; k++) {
				conditionList.push({ baseFactor, operator, compareFactor: compareFactor[k] });
			}
		}
	}

	return conditionList;
};

/**
 * Merges a custom upcharge list with an encoded string of other upcharges,
 * and returns the merged upcharges as an encoded string.
 *
 * @param {Array} customUpcharge - An array of custom upcharge objects, each
 * containing 'name', 'price', 'quantity', and 'group' properties.
 * @param {string} others - A Base64 encoded JSON string representing an array
 * of upcharge objects to be merged with the customUpcharge list.
 *
 * @returns {string} A Base64 encoded JSON string of the merged upcharges.
 * If 'others' is an empty string, only the customUpcharge list is returned.
 * If decoding or parsing 'others' fails, a console error is logged and only
 * the customUpcharge list is returned.
 */
export const mergeAndEncodeUpcharges = (customUpcharge, others) => {
	if (!customUpcharge.length) return others;

	const upchargeSummary = customUpcharge.map((item) => ({
		name: item.name,
		price: item.price,
		quantity: item.quantity,
		group: item.group,
	}));

	let mergedUpcharges;
	if ('' === others) {
		mergedUpcharges = upchargeSummary;
	} else {
		try {
			const decodedOthers = JSON.parse(window.atob(others));

			mergedUpcharges = [...decodedOthers, ...upchargeSummary];
		} catch (error) {
			console.error("Failed to decode or parse 'others':", error);
			mergedUpcharges = upchargeSummary;
		}
	}

	return window.btoa(JSON.stringify(mergedUpcharges));
};

/**
 * Retrieves the display names of colors for components of a specific type.
 *
 * @param {string} componentType - The type of components to filter by.
 * @param {Object} allComponents - All components of the shed.
 *
 * @returns {Object[]} An array of objects containing the keys and display names of colors.
 */
export const getColorDisplaysByComponentType = (componentType, allComponents) => {
	const displays = [];
	const components = Object.values(allComponents);

	for (const component of components) {
		if (component['type'] === componentType && Array.isArray(component['colors'])) {
			for (const color of component['colors']) {
				displays.push({ key: component['key'], name: color.display });
			}
		}
	}

	return displays;
};

/**
 * Retrieves the display names of colors of a specific type for all components.
 *
 * @param {string} colorType - The type of color to filter by.
 * @param {Object} allComponents - All components of the shed.
 *
 * @returns {Object[]} An array of objects containing the keys and display names of colors.
 */
export const getColorDisplaysByColorType = (colorType, allComponents) => {
	const displays = [];
	const components = Object.values(allComponents);

	for (const component of components) {
		if (Array.isArray(component['colors'])) {
			for (const color of component['colors']) {
				if (color.type === colorType) {
					displays.push({ key: component['key'], name: color.display });
				}
			}
		}
	}

	return displays;
};

/**
 * Retrieves the display names of colors for components of type 'shutters' and
 * components stored in the shuttersInObject.
 *
 * @param {Object} allComponents - All components of the shed.
 * @param {Object} shuttersInObject - Components stored in the shuttersInObject.
 * @returns {Object[]} An array of objects containing the keys and display names of colors.
 */
export const getAllShutterColorDisplays = (allComponents, shuttersInObject) => {
	const baseDisplays = getColorDisplaysByComponentType('shutters', allComponents);
	const components = Object.values(shuttersInObject);
	const additionalDisplays = [];

	for (const component of components) {
		if (Array.isArray(component['colors'])) {
			for (const color of component['colors']) {
				additionalDisplays.push({ key: component['key'], name: color.display });
			}
		}
	}

	return [...baseDisplays, ...additionalDisplays];
};
